import { useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { saveAs } from 'file-saver';
import { unparse } from 'papaparse';
import { Virtuoso } from 'react-virtuoso';
import { PracticeAnalyticsApi, PracticeAnalyticsTypes } from '@frontend/api-analytics';
import { TableCustomToolbarActions, UserCard } from '@frontend/analytics-shared';
import { EmptyStates } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { genUUIDV4 } from '@frontend/string';
import { theme } from '@frontend/theme';
import { Chip, ContentLoader, Tabs, useAlert } from '@frontend/design-system';
import { CustomCheckboxField } from '../custom-check-box-field';
import { TableActions } from '../table-actions';
import { BulkMessageButton } from './bulk-message-button';
import { isPatientScheduled } from './helpers/is-patient-scheduled';

type SelectedRows = Record<
  string,
  {
    [id: string]: PracticeAnalyticsTypes.PatientInfo;
  }
>;

export const SmallModePatientsList = <T extends string>({
  exportFileName = 'data',
  generateUniqueDataRenderingKeys,
  initialTab,
  isBulkMessagingEnabled,
  isDemoAccount,
  isExportEnabled,
  isLoadingLastContacted,
  isLoadingPatients,
  processExportableData,
  tabs,
  trackingIdBase,
}: PracticeAnalyticsTypes.SmallModePatientsListProps<T>) => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');
  const [activeTab, setActiveTab] = useState<T>(initialTab);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<SelectedRows>();

  const handleExport = useCallback(
    async (fileName: string, dataToExport: PracticeAnalyticsTypes.PatientInfo[]) => {
      setIsExporting(true);
      const isAuthorized =
        isExportEnabled && (await PracticeAnalyticsApi.auditPracticeAnalyticsExport(fileName.replaceAll('/', '-')));

      if (isAuthorized || isDemoAccount) {
        const csv = unparse(processExportableData?.(dataToExport) ?? dataToExport, {});
        saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), fileName + '.csv');
        setIsExporting(false);
      } else {
        alert.warning(t('You are not authorized to export data. This attempt has been logged.'));
        setIsExporting(false);
      }
    },
    [isDemoAccount, isExportEnabled, processExportableData, t, alert]
  );

  const handleToggleCheck = useCallback(
    (patient: PracticeAnalyticsTypes.PatientInfo, isChecked?: boolean) => {
      setSelectedRows((prev) => {
        const rows: SelectedRows = { ...(prev ?? Object.create(null)) };
        const currentTabRows = rows[activeTab] || {};

        if (isChecked) {
          currentTabRows[patient.id] = patient;
        } else {
          delete currentTabRows[patient.id];
        }

        return {
          ...rows,
          [activeTab]: currentTabRows,
        };
      });
    },
    [activeTab]
  );

  const handleSelectAll = useCallback(
    (isChecked?: boolean) => {
      if (isChecked) {
        setSelectedRows((prev) => ({
          ...prev,
          [activeTab]: Object.fromEntries(tabs[activeTab]?.data.map((patient) => [patient.id, patient])),
        }));
      } else {
        setSelectedRows((prev) => ({
          ...prev,
          [activeTab]: {},
        }));
      }
    },
    [activeTab, tabs]
  );

  return (
    <Tabs initialTab={initialTab} onChange={(id) => setActiveTab(id as T)}>
      <div css={styles.tabsWrapper}>
        <Tabs.Bar>
          {Object.entries(tabs as PracticeAnalyticsTypes.PatientsTabs<string>).map(([key, { label, trackingId }]) => {
            return (
              <Tabs.Tab id={key} key={key} trackingId={trackingId}>
                {label}
              </Tabs.Tab>
            );
          })}
        </Tabs.Bar>
        <TableCustomToolbarActions<PracticeAnalyticsTypes.PatientInfo>
          actions={BulkMessageButton}
          isExportReady={!!activeTab && !isLoadingPatients && !isLoadingLastContacted}
          isLoadingData={isLoadingPatients || isExporting}
          onExportClick={() =>
            handleExport(activeTab ? `${exportFileName} - ${activeTab}` : exportFileName, tabs[activeTab]?.data)
          }
          selection={selectedRows?.[activeTab] ? Object.values(selectedRows[activeTab]) : []}
          showExportIcon={isExportEnabled}
          style={{ flexWrap: 'nowrap', gap: theme.spacing(1) }}
          trackingIdBase={trackingIdBase}
        />
      </div>

      {Object.entries(tabs as PracticeAnalyticsTypes.PatientsTabs<string>).map(([key, { data = [] }]) => {
        return (
          <Tabs.Panel controller={key} key={key}>
            <VirtualList
              data={data}
              generateUniqueDataRenderingKeys={generateUniqueDataRenderingKeys}
              isBulkMessagingEnabled={isBulkMessagingEnabled}
              isDemoAccount={isDemoAccount}
              isLoadingPatients={isLoadingPatients}
              onCheckedChange={handleToggleCheck}
              onSelectAll={handleSelectAll}
              selectedRows={selectedRows?.[activeTab]}
            />
          </Tabs.Panel>
        );
      })}

      <ContentLoader show={isExporting} />
    </Tabs>
  );
};

interface VirtualListProps {
  data: PracticeAnalyticsTypes.PatientInfo[];
  generateUniqueDataRenderingKeys?: boolean;
  isBulkMessagingEnabled?: boolean;
  isDemoAccount?: boolean;
  isLoadingPatients?: boolean;
  onCheckedChange?: (patient: PracticeAnalyticsTypes.PatientInfo, isChecked?: boolean) => void;
  onSelectAll: (checked?: boolean) => void;
  selectedRows?: {
    [id: string]: PracticeAnalyticsTypes.PatientInfo;
  };
}

const VirtualList = ({
  data,
  generateUniqueDataRenderingKeys = true,
  isBulkMessagingEnabled,
  isDemoAccount,
  isLoadingPatients = true,
  onCheckedChange,
  onSelectAll,
  selectedRows,
}: VirtualListProps) => {
  const { t } = useTranslation('analytics');

  // Noticed that some datasets have repeated patients causing rendering issues.
  // Using a unique key for each item to ensure proper rendering.
  const dataWithUniqueKeys: (PracticeAnalyticsTypes.PatientInfo & {
    uniqueId?: string;
  })[] = useMemo(() => {
    if (!generateUniqueDataRenderingKeys) return data;

    return data.map((item) => ({
      ...item,
      uniqueId: genUUIDV4(),
    }));
  }, [data, generateUniqueDataRenderingKeys]);

  const selectedCount = Object.keys(selectedRows ?? {}).length;
  const totalCount = dataWithUniqueKeys.length;

  return (
    <>
      {!!dataWithUniqueKeys.length && (
        <CustomCheckboxField
          css={styles.selectAll}
          indeterminate={selectedCount > 0 && selectedCount < totalCount}
          label={t('Selected {{selected}} of {{total}}', {
            selected: selectedCount,
            total: totalCount,
          })}
          name='select-all-patients'
          onChange={onSelectAll}
          trackingId='pa-small-mode-select-all-patients'
          value={!!selectedCount}
        />
      )}
      <div css={styles.panel}>
        {isLoadingPatients ? (
          <ContentLoader message={t('Loading patients...')} show size='small' />
        ) : dataWithUniqueKeys.length ? (
          <Virtuoso
            computeItemKey={(_index, item) => item.uniqueId ?? item.id}
            context={{
              selectedRows,
              isBulkMessagingEnabled,
              isDemoAccount,
              onCheckedChange,
            }}
            data={dataWithUniqueKeys}
            itemContent={(_index, patient, context) => {
              const isScheduled = isPatientScheduled(patient, true);

              return (
                <div className='item' key={patient.id}>
                  {context.isBulkMessagingEnabled && (
                    <CustomCheckboxField
                      css={styles.checkbox}
                      label=''
                      name={`patient-${patient.id}-check`}
                      onChange={(isChecked) => context.onCheckedChange?.(patient, isChecked)}
                      value={!!context.selectedRows?.[patient.id]}
                    />
                  )}
                  <div className='details'>
                    <UserCard
                      firstName={patient.FirstName}
                      lastName={patient.LastName}
                      preferredName={patient.PreferredName}
                      openProfileOnClick={!context.isDemoAccount}
                      showOnlyName
                    />
                    <Chip variant={isScheduled ? 'success' : 'critical'} style={{ marginLeft: theme.spacing(6) }}>
                      {isScheduled ? t('Scheduled') : t('Unscheduled')}
                    </Chip>
                  </div>
                  <TableActions
                    isDemoAccount={context.isDemoAccount}
                    personId={patient.id}
                    personName={patient.FirstName}
                    phoneNumber={patient.MobilePhone || patient.HomePhone}
                    trackingIdBase='active-patient-small-mode'
                  />
                </div>
              );
            }}
            totalCount={dataWithUniqueKeys?.length ?? 0}
          />
        ) : (
          <EmptyStates
            emptyStateConfig={{
              header: t('No patients found'),
            }}
          />
        )}
      </div>
    </>
  );
};

const styles = {
  tabsWrapper: css`
    align-items: flex-end;
    display: flex;
    gap: ${theme.spacing(1)};
    justify-content: space-between;
    margin-bottom: ${theme.spacing(2)};

    > div:first-of-type {
      max-width: calc(100% - 164px);
    }
  `,

  panel: css`
    height: 500px;
    position: relative;

    .item {
      align-items: flex-start;
      border-bottom: 1px solid ${theme.colors.neutral20};
      display: flex;
      gap: ${theme.spacing(2)};
      padding: ${theme.spacing(2, 0, 2, 1)};

      .details {
        flex: 1;
        min-width: 0;
      }
    }
  `,

  checkbox: css`
    margin-top: ${theme.spacing(1.25)};
  `,

  selectAll: css`
    padding: ${theme.spacing(2, 1)};
  `,
};
